// components/Form2316Form.js
import React, { useState, useEffect } from 'react';
import form2316API from '../api/form2316API';
import ChartOfAccountsSearch from './ChartOfAccountsSearch';
import { formatCurrency, formatNumber } from '../utils/numberUtils';

const Form2316Form = ({ client, onRecordAdded, selectedYear }) => {
    const [record, setRecord] = useState({
        employeeName: '',
        employeeTin: '',
        employeeAddress: '',
        employeeZipCode: '',
        employeeCivilStatus: 'Single',
        employeeExemptionStatus: 'S/ME',
        employeeId: '',
        position: '',
        frequencyOfPayment: 'Monthly',
        
        employerName: client.businessName || client.name || '',
        employerTin: client.tinNumber || '',
        employerAddress: client.address || '',
        employerZipCode: client.zipCode || '',
        employerRdoCode: client.rdoCode || '',
        
        year: selectedYear || new Date().getFullYear(),
        periodCovered: 'January-December',
        dateOfEmployment: '',
        dateOfSeparation: '',
        
        basicSalary: '',
        overtimePay: '',
        holidayPay: '',
        nightShiftDifferential: '',
        hazardPay: '',
        thirteenthMonthPay: '',
        otherAllowances: '',
        deMinimisBenefits: '',
        otherBenefits: '',
        
        sssContributions: '',
        philhealthContributions: '',
        pagibigContributions: '',
        unionDues: '',
        
        taxWithheld: '',
        taxTableUsed: '2023 and onwards',
        
        chartOfAccount: null,
        chartOfAccountName: '',
        status: 'Draft',
        remarks: ''
    });

    const [submitting, setSubmitting] = useState(false);
    const [showBatchForm, setShowBatchForm] = useState(false);
    const [batchRecords, setBatchRecords] = useState([{ ...record }]);
    const [calculatedTax, setCalculatedTax] = useState({ taxDue: 0, netTaxableCompensation: 0 });

    // Update year when selectedYear changes
    useEffect(() => {
        if (selectedYear) {
            setRecord(prev => ({ ...prev, year: selectedYear }));
        }
    }, [selectedYear]);

    // Civil status options
    const civilStatusOptions = form2316API.getCivilStatusOptions();
    
    // Exemption status options
    const exemptionStatusOptions = form2316API.getExemptionStatusOptions();
    
    // Period covered options
    const periodCoveredOptions = form2316API.getPeriodCoveredOptions();
    
    // Frequency of payment options
    const frequencyOptions = form2316API.getFrequencyOfPaymentOptions();
    
    // Tax table options
    const taxTableOptions = form2316API.getTaxTableOptions();

    const handleChange = (field, value) => {
        const newRecord = { ...record, [field]: value };
        setRecord(newRecord);
        
        // Calculate tax if compensation fields change
        if (field.includes('Salary') || field.includes('Pay') || field.includes('Allowances') || 
            field.includes('Benefits') || field.includes('Contributions') || field === 'employeeExemptionStatus') {
            calculateTax(newRecord);
        }
    };

    const calculateTax = (recordData) => {
        try {
            // Calculate gross compensation
            const grossCompensation = 
                (parseFloat(recordData.basicSalary) || 0) +
                (parseFloat(recordData.overtimePay) || 0) +
                (parseFloat(recordData.holidayPay) || 0) +
                (parseFloat(recordData.nightShiftDifferential) || 0) +
                (parseFloat(recordData.hazardPay) || 0) +
                (parseFloat(recordData.thirteenthMonthPay) || 0) +
                (parseFloat(recordData.otherAllowances) || 0) +
                (parseFloat(recordData.otherBenefits) || 0);

            // Calculate deductions
            const totalDeductions = 
                (parseFloat(recordData.sssContributions) || 0) +
                (parseFloat(recordData.philhealthContributions) || 0) +
                (parseFloat(recordData.pagibigContributions) || 0) +
                (parseFloat(recordData.unionDues) || 0);

            // Calculate net taxable compensation
            const exemptionAmount = form2316API.calculateTaxLocal(0, recordData.employeeExemptionStatus).exemptionAmount;
            const netTaxableCompensation = Math.max(0, grossCompensation - totalDeductions - exemptionAmount);
            
            // Calculate tax due
            const taxCalculation = form2316API.calculateTaxLocal(grossCompensation - totalDeductions, recordData.employeeExemptionStatus);
            
            setCalculatedTax({
                grossCompensation: grossCompensation,
                totalDeductions: totalDeductions,
                exemptionAmount: exemptionAmount,
                netTaxableCompensation: taxCalculation.netTaxableIncome,
                taxDue: taxCalculation.taxDue
            });
        } catch (error) {
            console.error('Error calculating tax:', error);
        }
    };

    const handleChartOfAccountSelect = (account) => {
        if (!account) {
            setRecord(prev => ({
                ...prev,
                chartOfAccount: null,
                chartOfAccountName: ''
            }));
        } else {
            setRecord(prev => ({
                ...prev,
                chartOfAccount: account._id,
                chartOfAccountName: `${account.accountNumber} - ${account.accountName}`
            }));
        }
    };

    const validateRecord = () => {
        if (!record.employeeName.trim()) {
            alert('Employee name is required');
            return false;
        }
        if (!record.employeeTin) {
            alert('Employee TIN is required');
            return false;
        }
        if (!record.year) {
            alert('Year is required');
            return false;
        }
        if (record.year < 2000 || record.year > 2100) {
            alert('Year must be between 2000 and 2100');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateRecord()) {
            return;
        }

        try {
            setSubmitting(true);
            
            const recordData = {
                ...record,
                basicSalary: parseFloat(record.basicSalary) || 0,
                overtimePay: parseFloat(record.overtimePay) || 0,
                holidayPay: parseFloat(record.holidayPay) || 0,
                nightShiftDifferential: parseFloat(record.nightShiftDifferential) || 0,
                hazardPay: parseFloat(record.hazardPay) || 0,
                thirteenthMonthPay: parseFloat(record.thirteenthMonthPay) || 0,
                otherAllowances: parseFloat(record.otherAllowances) || 0,
                deMinimisBenefits: parseFloat(record.deMinimisBenefits) || 0,
                otherBenefits: parseFloat(record.otherBenefits) || 0,
                sssContributions: parseFloat(record.sssContributions) || 0,
                philhealthContributions: parseFloat(record.philhealthContributions) || 0,
                pagibigContributions: parseFloat(record.pagibigContributions) || 0,
                unionDues: parseFloat(record.unionDues) || 0,
                taxWithheld: parseFloat(record.taxWithheld) || 0,
                year: parseInt(record.year)
            };

            await form2316API.createRecord(client._id, recordData);
            
            // Reset form
            setRecord({
                employeeName: '',
                employeeTin: '',
                employeeAddress: '',
                employeeZipCode: '',
                employeeCivilStatus: 'Single',
                employeeExemptionStatus: 'S/ME',
                employeeId: '',
                position: '',
                frequencyOfPayment: 'Monthly',
                
                employerName: client.businessName || client.name || '',
                employerTin: client.tinNumber || '',
                employerAddress: client.address || '',
                employerZipCode: client.zipCode || '',
                employerRdoCode: client.rdoCode || '',
                
                year: selectedYear || new Date().getFullYear(),
                periodCovered: 'January-December',
                dateOfEmployment: '',
                dateOfSeparation: '',
                
                basicSalary: '',
                overtimePay: '',
                holidayPay: '',
                nightShiftDifferential: '',
                hazardPay: '',
                thirteenthMonthPay: '',
                otherAllowances: '',
                deMinimisBenefits: '',
                otherBenefits: '',
                
                sssContributions: '',
                philhealthContributions: '',
                pagibigContributions: '',
                unionDues: '',
                
                taxWithheld: '',
                taxTableUsed: '2023 and onwards',
                
                chartOfAccount: null,
                chartOfAccountName: '',
                status: 'Draft',
                remarks: ''
            });
            
            if (onRecordAdded) {
                onRecordAdded();
            }
            
            alert('Form 2316 record created successfully!');
        } catch (error) {
            console.error('Error creating Form 2316 record:', error);
            alert('Failed to create record: ' + (error.message || 'Unknown error'));
        } finally {
            setSubmitting(false);
        }
    };

    // Batch form functions
    const handleBatchRecordChange = (index, field, value) => {
        const newRecords = [...batchRecords];
        newRecords[index] = { ...newRecords[index], [field]: value };
        setBatchRecords(newRecords);
    };

    const handleAddBatchRow = () => {
        setBatchRecords([...batchRecords, { ...record }]);
    };

    const handleRemoveBatchRow = (index) => {
        if (batchRecords.length > 1) {
            const newRecords = [...batchRecords];
            newRecords.splice(index, 1);
            setBatchRecords(newRecords);
        }
    };

    const handleBatchSubmit = async (e) => {
        e.preventDefault();
        
        if (!selectedYear) {
            alert('Please select a year');
            return;
        }

        const validRecords = batchRecords.filter(r => 
            r.employeeName.trim() && r.employeeTin
        );

        if (validRecords.length === 0) {
            alert('Please add at least one valid record with employee name and TIN');
            return;
        }

        try {
            setSubmitting(true);
            
            // Format records
            const formattedRecords = validRecords.map(r => ({
                employeeName: r.employeeName,
                employeeTin: r.employeeTin,
                employeeAddress: r.employeeAddress || '',
                employeeZipCode: r.employeeZipCode || '',
                employeeCivilStatus: r.employeeCivilStatus,
                employeeExemptionStatus: r.employeeExemptionStatus,
                employeeId: r.employeeId || '',
                position: r.position || '',
                frequencyOfPayment: r.frequencyOfPayment,
                
                employerName: r.employerName || client.businessName || client.name || '',
                employerTin: r.employerTin || client.tinNumber || '',
                employerAddress: r.employerAddress || client.address || '',
                employerZipCode: r.employerZipCode || client.zipCode || '',
                employerRdoCode: r.employerRdoCode || client.rdoCode || '',
                
                periodCovered: r.periodCovered,
                dateOfEmployment: r.dateOfEmployment || '',
                dateOfSeparation: r.dateOfSeparation || '',
                
                basicSalary: parseFloat(r.basicSalary) || 0,
                overtimePay: parseFloat(r.overtimePay) || 0,
                holidayPay: parseFloat(r.holidayPay) || 0,
                nightShiftDifferential: parseFloat(r.nightShiftDifferential) || 0,
                hazardPay: parseFloat(r.hazardPay) || 0,
                thirteenthMonthPay: parseFloat(r.thirteenthMonthPay) || 0,
                otherAllowances: parseFloat(r.otherAllowances) || 0,
                deMinimisBenefits: parseFloat(r.deMinimisBenefits) || 0,
                otherBenefits: parseFloat(r.otherBenefits) || 0,
                
                sssContributions: parseFloat(r.sssContributions) || 0,
                philhealthContributions: parseFloat(r.philhealthContributions) || 0,
                pagibigContributions: parseFloat(r.pagibigContributions) || 0,
                unionDues: parseFloat(r.unionDues) || 0,
                
                taxWithheld: parseFloat(r.taxWithheld) || 0,
                taxTableUsed: r.taxTableUsed,
                
                chartOfAccount: r.chartOfAccount,
                chartOfAccountName: r.chartOfAccountName || '',
                status: 'Draft',
                remarks: r.remarks || ''
            }));

            await form2316API.createBatch(client._id, formattedRecords, selectedYear);
            
            // Reset batch form
            setBatchRecords([{ ...record }]);
            
            if (onRecordAdded) {
                onRecordAdded();
            }
            
            alert(`Successfully created ${validRecords.length} Form 2316 record(s)`);
            setShowBatchForm(false);
        } catch (error) {
            console.error('Error creating batch Form 2316 records:', error);
            alert('Failed to create records: ' + (error.message || 'Unknown error'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="text-lg font-semibold text-teal-700">Create Form 2316 Record</h3>
                        <p className="text-sm text-gray-600 mt-1">
                            Certificate of Compensation Payment/Tax Withheld (BIR Form 2316)
                        </p>
                        <div className="mt-2 text-xs text-gray-500">
                            Year {selectedYear || '?'} • {client.name}
                        </div>
                    </div>
                    <div className="flex space-x-3">
                        <button
                            type="button"
                            onClick={() => setShowBatchForm(!showBatchForm)}
                            className="px-4 py-2 text-sm text-teal-600 hover:text-teal-800 hover:bg-teal-100 border border-teal-300 rounded-md transition-colors"
                        >
                            {showBatchForm ? 'Single Entry' : 'Batch Entry'}
                        </button>
                        <button
                            type="button"
                            onClick={() => calculateTax(record)}
                            className="px-4 py-2 text-sm text-green-600 hover:text-green-800 hover:bg-green-100 border border-green-300 rounded-md transition-colors"
                        >
                            Calculate Tax
                        </button>
                    </div>
                </div>
            </div>
            
            {!showBatchForm ? (
                // Single Entry Form
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Employee Information */}
                        <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                            <h4 className="text-sm font-semibold text-teal-800 mb-4">Employee Information</h4>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Employee Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={record.employeeName}
                                            onChange={(e) => handleChange('employeeName', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                            placeholder="Last Name, First Name Middle Name"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Employee TIN *
                                        </label>
                                        <input
                                            type="text"
                                            value={record.employeeTin}
                                            onChange={(e) => handleChange('employeeTin', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                            placeholder="XXX-XXX-XXX-XXX"
                                            required
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-xs font-medium text-teal-700 mb-1">
                                        Employee Address
                                    </label>
                                    <input
                                        type="text"
                                        value={record.employeeAddress}
                                        onChange={(e) => handleChange('employeeAddress', e.target.value)}
                                        className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="Complete address"
                                    />
                                </div>
                                
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Civil Status
                                        </label>
                                        <select
                                            value={record.employeeCivilStatus}
                                            onChange={(e) => handleChange('employeeCivilStatus', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 bg-white"
                                        >
                                            {civilStatusOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Exemption Status
                                        </label>
                                        <select
                                            value={record.employeeExemptionStatus}
                                            onChange={(e) => handleChange('employeeExemptionStatus', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 bg-white"
                                        >
                                            {exemptionStatusOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Frequency of Payment
                                        </label>
                                        <select
                                            value={record.frequencyOfPayment}
                                            onChange={(e) => handleChange('frequencyOfPayment', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 bg-white"
                                        >
                                            {frequencyOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Employee ID
                                        </label>
                                        <input
                                            type="text"
                                            value={record.employeeId}
                                            onChange={(e) => handleChange('employeeId', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                            placeholder="Employee ID"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Position
                                        </label>
                                        <input
                                            type="text"
                                            value={record.position}
                                            onChange={(e) => handleChange('position', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                            placeholder="Job position"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Employer Information */}
                        <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                            <h4 className="text-sm font-semibold text-teal-800 mb-4">Employer Information</h4>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Employer Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={record.employerName}
                                            onChange={(e) => handleChange('employerName', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Employer TIN *
                                        </label>
                                        <input
                                            type="text"
                                            value={record.employerTin}
                                            onChange={(e) => handleChange('employerTin', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                            placeholder="XXX-XXX-XXX-XXX"
                                            required
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-xs font-medium text-teal-700 mb-1">
                                        Employer Address
                                    </label>
                                    <input
                                        type="text"
                                        value={record.employerAddress}
                                        onChange={(e) => handleChange('employerAddress', e.target.value)}
                                        className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                    />
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            RDO Code
                                        </label>
                                        <input
                                            type="text"
                                            value={record.employerRdoCode}
                                            onChange={(e) => handleChange('employerRdoCode', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                            placeholder="e.g., 043"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Period Covered
                                        </label>
                                        <select
                                            value={record.periodCovered}
                                            onChange={(e) => handleChange('periodCovered', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 bg-white"
                                        >
                                            {periodCoveredOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Date of Employment
                                        </label>
                                        <input
                                            type="date"
                                            value={record.dateOfEmployment}
                                            onChange={(e) => handleChange('dateOfEmployment', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Date of Separation
                                        </label>
                                        <input
                                            type="date"
                                            value={record.dateOfSeparation}
                                            onChange={(e) => handleChange('dateOfSeparation', e.target.value)}
                                            className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Compensation Details */}
                    <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                        <h4 className="text-sm font-semibold text-teal-800 mb-4">Compensation Details (Part I - Gross Compensation Income)</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-teal-700 mb-1">
                                    Basic Salary
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-teal-500 sm:text-sm">₱</span>
                                    </div>
                                    <input
                                        type="number"
                                        value={record.basicSalary}
                                        onChange={(e) => handleChange('basicSalary', e.target.value)}
                                        className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-medium text-teal-700 mb-1">
                                    Overtime Pay
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-teal-500 sm:text-sm">₱</span>
                                    </div>
                                    <input
                                        type="number"
                                        value={record.overtimePay}
                                        onChange={(e) => handleChange('overtimePay', e.target.value)}
                                        className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-medium text-teal-700 mb-1">
                                    Holiday Pay
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-teal-500 sm:text-sm">₱</span>
                                    </div>
                                    <input
                                        type="number"
                                        value={record.holidayPay}
                                        onChange={(e) => handleChange('holidayPay', e.target.value)}
                                        className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-medium text-teal-700 mb-1">
                                    13th Month Pay
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-teal-500 sm:text-sm">₱</span>
                                    </div>
                                    <input
                                        type="number"
                                        value={record.thirteenthMonthPay}
                                        onChange={(e) => handleChange('thirteenthMonthPay', e.target.value)}
                                        className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-medium text-teal-700 mb-1">
                                    Other Allowances
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-teal-500 sm:text-sm">₱</span>
                                    </div>
                                    <input
                                        type="number"
                                        value={record.otherAllowances}
                                        onChange={(e) => handleChange('otherAllowances', e.target.value)}
                                        className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-medium text-teal-700 mb-1">
                                    De Minimis Benefits
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-teal-500 sm:text-sm">₱</span>
                                    </div>
                                    <input
                                        type="number"
                                        value={record.deMinimisBenefits}
                                        onChange={(e) => handleChange('deMinimisBenefits', e.target.value)}
                                        className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-medium text-teal-700 mb-1">
                                    Other Benefits
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-teal-500 sm:text-sm">₱</span>
                                    </div>
                                    <input
                                        type="number"
                                        value={record.otherBenefits}
                                        onChange={(e) => handleChange('otherBenefits', e.target.value)}
                                        className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-medium text-teal-700 mb-1">
                                    Chart of Account
                                </label>
                                <ChartOfAccountsSearch
                                    value={record.chartOfAccountName}
                                    onChange={(value) => handleChange('chartOfAccountName', value)}
                                    onSelect={(account) => handleChartOfAccountSelect(account)}
                                    placeholder="Search chart of accounts"
                                    showLinkedBadge={false}
                                />
                            </div>
                        </div>
                    </div>
                    
                    {/* Deductions and Tax Computation */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Deductions (Part II) */}
                        <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                            <h4 className="text-sm font-semibold text-teal-800 mb-4">Deductions (Part II - Non-Taxable Compensation)</h4>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            SSS Contributions
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <span className="text-teal-500 sm:text-sm">₱</span>
                                            </div>
                                            <input
                                                type="number"
                                                value={record.sssContributions}
                                                onChange={(e) => handleChange('sssContributions', e.target.value)}
                                                className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                                placeholder="0.00"
                                                step="0.01"
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            PhilHealth Contributions
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <span className="text-teal-500 sm:text-sm">₱</span>
                                            </div>
                                            <input
                                                type="number"
                                                value={record.philhealthContributions}
                                                onChange={(e) => handleChange('philhealthContributions', e.target.value)}
                                                className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                                placeholder="0.00"
                                                step="0.01"
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Pag-IBIG Contributions
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <span className="text-teal-500 sm:text-sm">₱</span>
                                            </div>
                                            <input
                                                type="number"
                                                value={record.pagibigContributions}
                                                onChange={(e) => handleChange('pagibigContributions', e.target.value)}
                                                className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                                placeholder="0.00"
                                                step="0.01"
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-teal-700 mb-1">
                                            Union Dues
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <span className="text-teal-500 sm:text-sm">₱</span>
                                            </div>
                                            <input
                                                type="number"
                                                value={record.unionDues}
                                                onChange={(e) => handleChange('unionDues', e.target.value)}
                                                className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                                placeholder="0.00"
                                                step="0.01"
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-xs font-medium text-teal-700 mb-1">
                                        Tax Withheld (Part IV)
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="text-teal-500 sm:text-sm">₱</span>
                                        </div>
                                        <input
                                            type="number"
                                            value={record.taxWithheld}
                                            onChange={(e) => handleChange('taxWithheld', e.target.value)}
                                            className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                            placeholder="0.00"
                                            step="0.01"
                                            min="0"
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-xs font-medium text-teal-700 mb-1">
                                        Tax Table Used
                                    </label>
                                    <select
                                        value={record.taxTableUsed}
                                        onChange={(e) => handleChange('taxTableUsed', e.target.value)}
                                        className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 bg-white"
                                    >
                                        {taxTableOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        
                        {/* Tax Computation Summary */}
                        <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                            <h4 className="text-sm font-semibold text-teal-800 mb-4">Tax Computation Summary</h4>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-teal-700">Gross Compensation:</span>
                                    <span className="text-sm font-medium text-teal-900">
                                        {formatCurrency(calculatedTax.grossCompensation || 0)}
                                    </span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-teal-700">Total Deductions:</span>
                                    <span className="text-sm font-medium text-teal-900">
                                        {formatCurrency(calculatedTax.totalDeductions || 0)}
                                    </span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-teal-700">Exemption Amount:</span>
                                    <span className="text-sm font-medium text-green-600">
                                        {formatCurrency(calculatedTax.exemptionAmount || 0)}
                                    </span>
                                </div>
                                
                                <div className="border-t border-teal-300 pt-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-semibold text-teal-800">Net Taxable Compensation:</span>
                                        <span className="text-sm font-semibold text-teal-900">
                                            {formatCurrency(calculatedTax.netTaxableCompensation || 0)}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="border-t border-teal-300 pt-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-semibold text-teal-800">Tax Due:</span>
                                        <span className="text-sm font-semibold text-red-600">
                                            {formatCurrency(calculatedTax.taxDue || 0)}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="border-t border-teal-300 pt-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-semibold text-teal-800">Tax Withheld:</span>
                                        <span className="text-sm font-semibold text-teal-900">
                                            {formatCurrency(parseFloat(record.taxWithheld) || 0)}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="border-t border-teal-300 pt-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-semibold text-teal-800">Tax Refund/(Payable):</span>
                                        <span className={`text-sm font-semibold ${
                                            (parseFloat(record.taxWithheld) || 0) > (calculatedTax.taxDue || 0) 
                                                ? 'text-green-600' 
                                                : 'text-red-600'
                                        }`}>
                                            {formatCurrency(
                                                (parseFloat(record.taxWithheld) || 0) - (calculatedTax.taxDue || 0)
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mt-4">
                                <label className="block text-xs font-medium text-teal-700 mb-1">
                                    Remarks
                                </label>
                                <textarea
                                    value={record.remarks}
                                    onChange={(e) => handleChange('remarks', e.target.value)}
                                    className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                    placeholder="Additional notes or remarks"
                                    rows="2"
                                />
                            </div>
                        </div>
                    </div>
                    
                    {/* Submit Button */}
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                        >
                            {submitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Creating...
                                </>
                            ) : (
                                'Create Form 2316 Record'
                            )}
                        </button>
                    </div>
                </form>
            ) : (
                // Batch Entry Form
                <div className="space-y-6">
                    <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                        <h4 className="text-sm font-semibold text-teal-800 mb-4">Batch Form 2316 Entry</h4>
                        <p className="text-sm text-teal-600 mb-4">
                            Create multiple Form 2316 records at once. Employee name and TIN are required for each record.
                        </p>
                        
                        <div className="space-y-4">
                            {batchRecords.map((batchRecord, index) => (
                                <div key={index} className="bg-white p-4 rounded border border-teal-200">
                                    <div className="flex justify-between items-center mb-3">
                                        <h5 className="text-sm font-medium text-teal-700">
                                            Employee {index + 1} of {batchRecords.length}
                                        </h5>
                                        {batchRecords.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveBatchRow(index)}
                                                className="text-xs text-red-600 hover:text-red-800"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-teal-700 mb-1">
                                                Employee Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={batchRecord.employeeName}
                                                onChange={(e) => handleBatchRecordChange(index, 'employeeName', e.target.value)}
                                                className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                                placeholder="Last Name, First Name"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-teal-700 mb-1">
                                                Employee TIN *
                                            </label>
                                            <input
                                                type="text"
                                                value={batchRecord.employeeTin}
                                                onChange={(e) => handleBatchRecordChange(index, 'employeeTin', e.target.value)}
                                                className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                                placeholder="XXX-XXX-XXX-XXX"
                                                required
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4 mt-3">
                                        <div>
                                            <label className="block text-xs font-medium text-teal-700 mb-1">
                                                Civil Status
                                            </label>
                                            <select
                                                value={batchRecord.employeeCivilStatus}
                                                onChange={(e) => handleBatchRecordChange(index, 'employeeCivilStatus', e.target.value)}
                                                className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 bg-white"
                                            >
                                                {civilStatusOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-teal-700 mb-1">
                                                Exemption Status
                                            </label>
                                            <select
                                                value={batchRecord.employeeExemptionStatus}
                                                onChange={(e) => handleBatchRecordChange(index, 'employeeExemptionStatus', e.target.value)}
                                                className="w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 bg-white"
                                            >
                                                {exemptionStatusOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4 mt-3">
                                        <div>
                                            <label className="block text-xs font-medium text-teal-700 mb-1">
                                                Basic Salary
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <span className="text-teal-500 sm:text-sm">₱</span>
                                                </div>
                                                <input
                                                    type="number"
                                                    value={batchRecord.basicSalary}
                                                    onChange={(e) => handleBatchRecordChange(index, 'basicSalary', e.target.value)}
                                                    className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                                    placeholder="0.00"
                                                    step="0.01"
                                                    min="0"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-teal-700 mb-1">
                                                Tax Withheld
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <span className="text-teal-500 sm:text-sm">₱</span>
                                                </div>
                                                <input
                                                    type="number"
                                                    value={batchRecord.taxWithheld}
                                                    onChange={(e) => handleBatchRecordChange(index, 'taxWithheld', e.target.value)}
                                                    className="pl-7 w-full border border-teal-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                                                    placeholder="0.00"
                                                    step="0.01"
                                                    min="0"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        <div className="mt-4 flex justify-between items-center">
                            <button
                                type="button"
                                onClick={handleAddBatchRow}
                                className="px-4 py-2 text-sm text-teal-600 hover:text-teal-800 hover:bg-teal-100 border border-teal-300 rounded-md transition-colors"
                            >
                                + Add Another Employee
                            </button>
                            
                            <div className="text-xs text-teal-600">
                                {batchRecords.length} employee(s) • Maximum 10 per batch
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => setShowBatchForm(false)}
                            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-gray-300 rounded-md transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleBatchSubmit}
                            disabled={submitting || !selectedYear}
                            className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                        >
                            {submitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Creating...
                                </>
                            ) : (
                                `Create ${batchRecords.length} Form 2316 Records`
                            )}
                        </button>
                    </div>
                </div>
            )}
            
        </div>
    );
};

export default Form2316Form;